import { QueryAction, QueryStatus } from 'services/types';
import { QueryState } from 'services/utils/QueryState';

import {
  SoftwareInventoryFilters,
  SoftwareInstallationPage,
  SoftwarePackagePage,
  SoftwareInventorySummary,
} from 'app/api/services/scanVaultSoftware';
import { createSlice } from 'utils/@reduxjs/toolkit';

import { emptySoftwareInventorySummary, SoftwareInventoryState } from './types';

export const initialState: SoftwareInventoryState = {
  summary: QueryState.init(emptySoftwareInventorySummary),
  packages: QueryState.init([]),
  packagesNextCursor: undefined,
  isLoadingMorePackages: false,
  installations: QueryState.init([]),
  // Removed the unused `filter` state (written but never read) and its updateFilter action.
};

const softwareInventorySlice = createSlice({
  name: 'softwareInventory',
  initialState,
  reducers: {
    getSoftwareInventory(
      state,
      action: QueryAction<SoftwareInventorySummary, SoftwareInventoryFilters>,
    ) {
      state.summary = QueryState.next(state.summary, action);
    },
    getSoftwareInstallations(
      state,
      action: QueryAction<SoftwareInstallationPage, SoftwareInventoryFilters>,
    ) {
      state.installations = QueryState.next(state.installations, action, {
        mapData: response => response?.items ?? [],
      });
    },
    getSoftwarePackages(
      state,
      action: QueryAction<SoftwarePackagePage, SoftwareInventoryFilters>,
    ) {
      const isLoadMore = Boolean(action.payload.q?.cursor);
      const previousPackages = state.packages.data;

      state.packages = QueryState.next(state.packages, action, {
        mapData: response =>
          isLoadMore
            ? [...previousPackages, ...(response?.items ?? [])]
            : response?.items ?? [],
      });

      if (isLoadMore && !action.payload.data) {
        state.packages.data = previousPackages;
      }

      if (action.payload.status === QueryStatus.pending) {
        state.isLoadingMorePackages = isLoadMore;
        if (!isLoadMore) state.packagesNextCursor = undefined;
      }

      if (action.payload.status === QueryStatus.fulfilled) {
        state.packagesNextCursor = action.payload.data?.next_cursor;
        state.isLoadingMorePackages = false;
      }

      if (action.payload.status === QueryStatus.failed) {
        state.isLoadingMorePackages = false;
      }

      if (!isLoadMore && action.payload.status !== QueryStatus.fulfilled) {
        state.packagesNextCursor = undefined;
        state.isLoadingMorePackages = false;
      }

      if (isLoadMore && !action.payload.status) {
        state.isLoadingMorePackages = true;
      }
    },
  },
});

export const { actions, reducer, name: sliceKey } = softwareInventorySlice;
